import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import {
  UsersFilterParams,
  UserType,
  UsersListResponse,
  CreateUserParams,
  UpdateUserParams,
  SendEmailVerificationParams
} from '@/types/user'
import { BulkDeleteResponse } from '@/types/general'
import { getErrorMessage } from '@/lib/utils'
import { SortEnum } from '@/enums'
import {
  fetchUsers,
  fetchUser,
  updateUser,
  deleteUser,
  createUser,
  bulkDeleteUsers,
  sendEmailVerification
} from '@/lib/api'
import useMeStore from '@/store/me'

// Single user state type
type UserStateType = {
  isLoading: boolean
  response: UserType | null
  error: {
    status?: number
    message: string
  } | null
}

// Users list state type
type UsersListStateType = {
  isLoading: boolean
  response: UsersListResponse | null
  error: {
    status?: number
    message: string
  } | null
}

type UsersStoreState = {
  // Users list filters state
  filters: UsersFilterParams
  setFilters: (filters: Partial<UsersFilterParams>) => void
  resetFilters: () => void
  updateUrlParams: (searchParams: URLSearchParams) => void
  getUrlParams: () => URLSearchParams

  // Users list state
  usersListState: UsersListStateType
  fetchUsers: (params?: UsersFilterParams) => Promise<UsersListResponse>
  resetUsersList: () => void

  // Single user state
  userState: UserStateType
  getUser: (userId: string) => Promise<UserType>
  createUser: (params: CreateUserParams) => Promise<UserType>
  updateUser: (userId: string, params: UpdateUserParams) => Promise<UserType>
  deleteUser: (userId: string) => Promise<void>
  resetUser: () => void

  // Selection operations
  selectedUserIds: string[]
  selectUser: (id: string) => void
  deselectUser: (id: string) => void
  toggleUserSelection: (id: string) => void
  selectAllUsers: (users: UserType[]) => void
  deselectAllUsers: () => void
  isUserSelected: (id: string) => boolean

  // Bulk delete operation
  bulkDeleteUsers: (ids: string[]) => Promise<BulkDeleteResponse>

  // Send email verification
  sendEmailVerification: (userId: string, params: SendEmailVerificationParams) => Promise<void>
}

const initialUserState: UserStateType = {
  isLoading: false,
  response: null,
  error: null
}

const initialUsersListState: UsersListStateType = {
  isLoading: false,
  response: null,
  error: null
}

const defaultFilters: UsersFilterParams = {
  page: 1,
  size: 20,
  sort: SortEnum.ASC,
  sortBy: 'createdAt',
  q: ''
}

const useUsersStore = create<UsersStoreState>()(
  devtools((set, get) => ({
    userState: initialUserState,
    usersListState: initialUsersListState,
    selectedUserIds: [],
    filters: { ...defaultFilters },

    setFilters: newFilters => {
      set(state => {
        const updatedFilters = { ...state.filters }

        Object.entries(newFilters).forEach(([key, value]) => {
          if (value === undefined || value === null || value === '') {
            delete updatedFilters[key as keyof UsersFilterParams]
          } else {
            updatedFilters[key as keyof UsersFilterParams] = value as any
          }
        })

        return { filters: updatedFilters }
      })
    },

    resetFilters: () => set({ filters: { ...defaultFilters } }),

    updateUrlParams: searchParams => {
      const filters: UsersFilterParams = {}

      for (const [key, value] of searchParams.entries()) {
        if (!value) {
          continue
        }

        if (key === 'page' || key === 'size') {
          filters[key] = parseInt(value)
        } else if (key === 'sort') {
          filters[key] = value as SortEnum
        } else if (key === 'sortBy') {
          filters[key] = value
        } else if (key === 'blocked' || key === 'approved') {
          filters[key] = value === 'true'
        } else if (key === 'roles') {
          filters[key] = value.split(',') as UsersFilterParams['roles']
        } else if (key === 'groups' || key === 'genders') {
          filters[key] = value.split(',') as any
        } else {
          filters[key as keyof UsersFilterParams] = value as any
        }
      }

      set({ filters })
    },

    getUrlParams: () => {
      const params = new URLSearchParams()
      const { filters } = get()

      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          if (Array.isArray(value)) {
            params.set(key, value.join(','))
          } else {
            params.set(key, String(value))
          }
        }
      })

      return params
    },

    // Single user operations
    getUser: async (userId: string) => {
      // If already loading, don't make another request
      const currentState = get().userState
      if (currentState.isLoading) {
        return currentState.response!
      }

      set(() => ({
        userState: {
          ...initialUserState,
          isLoading: true
        }
      }))

      try {
        const data = await fetchUser(userId)

        set(() => ({
          userState: {
            ...initialUserState,
            response: data
          }
        }))
        return data
      } catch (error: any) {
        set(() => ({
          userState: {
            ...initialUserState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    createUser: async (params: CreateUserParams) => {
      set(() => ({
        userState: {
          ...initialUserState,
          isLoading: true
        }
      }))

      try {
        const data = await createUser(params)

        set(() => ({
          userState: {
            ...initialUserState,
            response: data
          }
        }))

        return data
      } catch (error: any) {
        set(() => ({
          userState: {
            ...initialUserState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    updateUser: async (userId: string, params: UpdateUserParams) => {
      set(() => ({
        userState: {
          ...get().userState,
          isLoading: true
        }
      }))

      try {
        const data = await updateUser(userId, params)

        set(() => ({
          userState: {
            ...get().userState,
            isLoading: false,
            response: data
          }
        }))

        // Silently sync meState if the updated user is the current user
        const meState = useMeStore.getState().meState
        if (meState.response?.id === data.id) {
          useMeStore.setState({
            meState: {
              ...meState,
              response: data
            }
          })
        }

        return data
      } catch (error: any) {
        set(() => ({
          userState: {
            ...initialUserState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    deleteUser: async (userId: string) => {
      set(() => ({
        userState: {
          ...get().userState,
          isLoading: true
        }
      }))

      try {
        await deleteUser(userId)

        set(() => ({
          userState: initialUserState
        }))
      } catch (error: any) {
        set(() => ({
          userState: {
            ...initialUserState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    resetUser: () => {
      set(() => ({
        userState: initialUserState
      }))
    },

    // Users list operations
    fetchUsers: async (params?: UsersFilterParams) => {
      // If already loading, don't make another request
      const currentState = get().usersListState
      if (currentState.isLoading) {
        return currentState.response!
      }

      set(() => ({
        usersListState: {
          ...initialUsersListState,
          isLoading: true
        }
      }))

      try {
        // Use provided params or fall back to store filters
        const filtersToUse = params || get().filters
        const data = await fetchUsers(filtersToUse)

        set(() => ({
          usersListState: {
            ...initialUsersListState,
            response: data
          }
        }))
        return data
      } catch (error: any) {
        set(() => ({
          usersListState: {
            ...initialUsersListState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            }
          }
        }))
        throw error
      }
    },

    resetUsersList: () => {
      set(() => ({
        usersListState: initialUsersListState
      }))
    },

    // Selection operations
    selectUser: id => {
      set(state => {
        if (state.selectedUserIds.includes(id)) {
          return state
        }
        return { selectedUserIds: [...state.selectedUserIds, id] }
      })
    },

    deselectUser: id => {
      set(state => ({
        selectedUserIds: state.selectedUserIds.filter(userId => userId !== id)
      }))
    },

    toggleUserSelection: id => {
      set(state => {
        if (state.selectedUserIds.includes(id)) {
          return { selectedUserIds: state.selectedUserIds.filter(userId => userId !== id) }
        }
        return { selectedUserIds: [...state.selectedUserIds, id] }
      })
    },

    selectAllUsers: users => {
      set(() => ({
        selectedUserIds: users.map(user => user.id)
      }))
    },

    deselectAllUsers: () => {
      set(() => ({ selectedUserIds: [] }))
    },

    isUserSelected: id => {
      return get().selectedUserIds.includes(id)
    },

    // Bulk delete operation
    bulkDeleteUsers: async ids => {
      set(state => ({
        userState: {
          ...state.userState,
          isLoading: true,
          error: null
        }
      }))

      try {
        const response = await bulkDeleteUsers({ ids })

        set(state => ({
          userState: {
            ...state.userState,
            isLoading: false
          },
          // Only remove successfully deleted items from selection
          selectedUserIds: state.selectedUserIds.filter(id => !response.deletedItems.includes(id))
        }))

        return response
      } catch (error: any) {
        set(state => ({
          userState: {
            ...state.userState,
            error: {
              status: error.response?.status || 500,
              message: getErrorMessage(error)
            },
            isLoading: false
          }
        }))

        throw error
      }
    },

    sendEmailVerification: async (userId: string, params: SendEmailVerificationParams) => {
      await sendEmailVerification(userId, params)
    }
  }))
)

export default useUsersStore
